import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import SuperFinanceiro from "./SuperFinanceiro";
import { INVOICE_STATUS_LABELS, PAYMENT_METHOD_LABELS, getInvoiceStatusLabel } from "../../constants/invoiceStatus";

import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload.invoices || action.payload;
    const newInvoices = [];

    invoices.forEach((invoice) => {
      const invoiceIndex = state.findIndex((i) => i.id === invoice.id);
      if (invoiceIndex !== -1) {
        state[invoiceIndex] = invoice;
      } else {
        newInvoices.push(invoice);
      }
    });

    return [...state, ...newInvoices];
  }

  if (action.type === "UPDATE_INVOICES") {
    const invoice = action.payload;
    const invoiceIndex = state.findIndex((i) => i.id === invoice.id);

    if (invoiceIndex !== -1) {
      state[invoiceIndex] = invoice;
      return [...state];
    } else {
      return [invoice, ...state];
    }
  }

  if (action.type === "DELETE_INVOICE") {
    const invoiceId = action.payload;

    const invoiceIndex = state.findIndex((i) => i.id === invoiceId);
    if (invoiceIndex !== -1) {
      state.splice(invoiceIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  kpiCard: {
    padding: theme.spacing(2),
    textAlign: "center",
  },
  kpiValue: {
    fontSize: "1.6rem",
    fontWeight: 600,
  },
  kpiLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginTop: 2,
  },
  summaryItem: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 4,
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  filterControl: { minWidth: 160 },
  valorField: { width: 110 },
}));

// Boss view: super users see the multi-company dashboard instead of the
// single-company invoice list. Branching at this wrapper level (rather than
// inside AdminInvoices) keeps hooks-rules compliance — the chosen subtree
// owns its own hooks consistently across renders.
const Invoices = () => {
  const { user } = useContext(AuthContext);
  if (user?.super) {
    return <SuperFinanceiro />;
  }
  return <AdminInvoices />;
};

const formatBRL = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "R$ 0,00";
  return n.toLocaleString("pt-br", { style: "currency", currency: "BRL" });
};

// Same derivation PaymentView.js uses for its own status filter — one
// source of both wording and option set so this page can't drift from the
// admin views again (canceled excluded, matching that page's convention).
const STATUS_OPTIONS = Object.keys(INVOICE_STATUS_LABELS)
  .filter((key) => key !== "canceled")
  .map((key) => ({ value: key, label: INVOICE_STATUS_LABELS[key].label }));

const FORMA_OPTIONS = [
  ...Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({ value, label })),
  { value: "manual", label: "Manual" },
];

const AdminInvoices = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [invoices, dispatch] = useReducer(reducer, []);
  const [isCompanyExpired, setIsCompanyExpired] = useState(false);

  const [statusFilter, setStatusFilter] = useState([]);
  const [formaFilter, setFormaFilter] = useState([]);
  const [valorMin, setValorMin] = useState("");
  const [valorMax, setValorMax] = useState("");

  useEffect(() => {
    if (user && user.company) {
      const isExpired = moment().isAfter(moment(user.company.dueDate));
      setIsCompanyExpired(isExpired);
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const fetchInvoices = async () => {
      try {
        const { data } = await api.get("/invoices/all", {
          params: { pageNumber: 1, limit: 500 },
        });
        dispatch({ type: "LOAD_INVOICES", payload: data });
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    fetchInvoices();
  }, []);

  // "Forma" reflects how the customer actually pays (PIX/Boleto/Cartão),
  // sourced from Company.paymentFlow — the same field SuperFinanceiro.js /
  // CompanyFinanceiro.js already use via formatPaymentMethod(). It's set
  // once at checkout and doesn't vary per invoice (mid-subscription payment
  // method changes aren't offered), so every invoice for this company
  // shares it — except ones never linked to a real Asaas charge at all
  // (no asaasPaymentId), which stay "Manual" as before.
  const getInvoiceForma = (inv) => {
    if (!inv.asaasPaymentId) return "manual";
    return (user?.company?.paymentFlow || "").toUpperCase();
  };

  const rowStyle = (record) => {
    if (record.status === "overdue") {
      return { backgroundColor: "#ffbcbc9c" };
    }
  };

  const rowStatusLabel = (record) => getInvoiceStatusLabel(record.status).label;

  const kpis = useMemo(() => {
    const paid = invoices.filter((i) => i.status === "paid");
    const overdue = invoices.filter((i) => i.status === "overdue");
    const pending = invoices.filter((i) => i.status === "pending");
    const sum = (arr) => arr.reduce((s, i) => s + (Number(i.value) || 0), 0);
    return {
      paid: { count: paid.length, total: sum(paid) },
      overdue: { count: overdue.length, total: sum(overdue) },
      pending: { count: pending.length, total: sum(pending) },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      if (statusFilter.length > 0 && !statusFilter.includes(inv.status)) return false;
      if (formaFilter.length > 0 && !formaFilter.includes(getInvoiceForma(inv))) return false;
      const val = Number(inv.value) || 0;
      if (valorMin !== "" && val < parseFloat(valorMin)) return false;
      if (valorMax !== "" && val > parseFloat(valorMax)) return false;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, statusFilter, formaFilter, valorMin, valorMax]);

  if (user?.profile !== "admin") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Faturas ({invoices.length})</Title>
        {isCompanyExpired && (
          <div style={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            padding: "10px",
            borderRadius: "4px",
            marginTop: "10px",
            border: "1px solid #ef9a9a",
          }}>
            <strong>Atenção:</strong> Sua assinatura está vencida. Entre em contato com o suporte para regularizar sua situação.
          </div>
        )}
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">

        {/* KPI cards */}
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" className={classes.kpiCard}>
              <div className={classes.kpiValue} style={{ color: "#e65100" }}>
                {kpis.pending.count}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#e65100", fontWeight: 500 }}>
                {formatBRL(kpis.pending.total)}
              </div>
              <div className={classes.kpiLabel}>Pendentes</div>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" className={classes.kpiCard}>
              <div className={classes.kpiValue} style={{ color: "#c62828" }}>
                {kpis.overdue.count}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#c62828", fontWeight: 500 }}>
                {formatBRL(kpis.overdue.total)}
              </div>
              <div className={classes.kpiLabel}>Em atraso</div>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" className={classes.kpiCard}>
              <div className={classes.kpiValue} style={{ color: "#2e7d32" }}>
                {kpis.paid.count}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#2e7d32", fontWeight: 500 }}>
                {formatBRL(kpis.paid.total)}
              </div>
              <div className={classes.kpiLabel}>Pagos</div>
            </Paper>
          </Grid>
        </Grid>

        {/* Filters */}
        <div className={classes.filtersRow}>
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
              renderValue={(selected) => selected.map((v) => STATUS_OPTIONS.find((o) => o.value === v)?.label || v).join(", ")}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox checked={statusFilter.includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Forma</InputLabel>
            <Select
              multiple
              value={formaFilter}
              onChange={(e) => setFormaFilter(e.target.value)}
              label="Forma"
              renderValue={(selected) => selected.map((v) => FORMA_OPTIONS.find((o) => o.value === v)?.label || v).join(", ")}
            >
              {FORMA_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox checked={formaFilter.includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            size="small"
            label="Valor mín."
            className={classes.valorField}
            value={valorMin}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9.,]/g, "");
              setValorMin(v);
            }}
            inputProps={{ inputMode: "decimal" }}
          />
          <TextField
            variant="outlined"
            size="small"
            label="Valor máx."
            className={classes.valorField}
            value={valorMax}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9.,]/g, "");
              setValorMax(v);
            }}
            inputProps={{ inputMode: "decimal" }}
          />
        </div>

        {/* Summary totals — before the Asaas invoice table */}
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" className={classes.summaryItem} style={{ borderColor: "#2e7d32" }}>
              <Typography variant="caption" color="textSecondary">Valor total pago</Typography>
              <Typography variant="subtitle1" style={{ fontWeight: 600, color: "#2e7d32" }}>
                {formatBRL(kpis.paid.total)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" className={classes.summaryItem} style={{ borderColor: "#e65100" }}>
              <Typography variant="caption" color="textSecondary">Valor total pendente</Typography>
              <Typography variant="subtitle1" style={{ fontWeight: 600, color: "#e65100" }}>
                {formatBRL(kpis.pending.total)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" className={classes.summaryItem} style={{ borderColor: "#c62828" }}>
              <Typography variant="caption" color="textSecondary">Valor total em atraso</Typography>
              <Typography variant="subtitle1" style={{ fontWeight: 600, color: "#c62828" }}>
                {formatBRL(kpis.overdue.total)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Detalhes</TableCell>
              <TableCell align="center">Usuários</TableCell>
              <TableCell align="center">Conexões</TableCell>
              <TableCell align="center">Filas</TableCell>
              <TableCell align="center">Forma</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Valor</TableCell>
              <TableCell align="center">Data de Pagamento</TableCell>
              <TableCell align="center">Data de Vencimento</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices.map((inv) => (
              <TableRow style={rowStyle(inv)} key={inv.id}>
                <TableCell align="center">{inv.detail}</TableCell>
                <TableCell align="center">{inv.users}</TableCell>
                <TableCell align="center">{inv.connections}</TableCell>
                <TableCell align="center">{inv.queues}</TableCell>
                <TableCell align="center">
                  {FORMA_OPTIONS.find((o) => o.value === getInvoiceForma(inv))?.label || "—"}
                </TableCell>
                <TableCell style={{ fontWeight: "bold" }} align="center">
                  {formatBRL(inv.value)}
                </TableCell>
                <TableCell align="center">
                  {inv.paidAt ? moment(inv.paidAt).format("DD/MM/YYYY") : "—"}
                </TableCell>
                <TableCell align="center">{moment(inv.dueDate).format("DD/MM/YYYY")}</TableCell>
                <TableCell style={{ fontWeight: "bold" }} align="center">
                  {rowStatusLabel(inv)}
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={9} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Invoices;
